import React from 'react';

const s = {
  wrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '60px 24px', background: 'var(--void)', position: 'relative', overflow: 'hidden'
  },
  grid: {
    position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: 'linear-gradient(rgba(124,58,237,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(124,58,237,.06) 1px,transparent 1px)',
    backgroundSize: '56px 56px',
    maskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%,black 30%,transparent 100%)',
    WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 50%,black 30%,transparent 100%)',
  },
  orb1: {
    position: 'absolute', width: 520, height: 520, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(124,58,237,.18) 0%,transparent 70%)',
    filter: 'blur(70px)', top: -120, left: -120, zIndex: 0,
    animation: 'pulse 4s ease-in-out infinite'
  },
  orb2: {
    position: 'absolute', width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(6,182,212,.12) 0%,transparent 70%)',
    filter: 'blur(70px)', bottom: -100, right: -100, zIndex: 0,
    animation: 'pulse 4s 2s ease-in-out infinite'
  },
  inner: { position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(124,58,237,.12)', border: '1px solid rgba(124,58,237,.35)',
    borderRadius: 100, padding: '6px 18px',
    fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--violetbr)',
    letterSpacing: '.06em', marginBottom: 32
  },
  dot: {
    width: 7, height: 7, borderRadius: '50%', background: 'var(--violetbr)',
    boxShadow: '0 0 8px var(--violetbr)', animation: 'pulse 2s ease-in-out infinite'
  },
  logo: {
    fontFamily: 'var(--ff-display)', fontSize: 'clamp(52px,9vw,96px)',
    fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, marginBottom: 10,
    background: 'linear-gradient(135deg,#f0f0ff 0%,#c4b5fd 45%,#818cf8 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  tagline: {
    fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--cyan)',
    letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 28
  },
  desc: {
    fontSize: 18, color: 'var(--txt2)', maxWidth: 600,
    textAlign: 'center', lineHeight: 1.7, marginBottom: 44
  },
  steps: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 44, flexWrap: 'wrap', justifyContent: 'center' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--txt2)' },
  stepNum: {
    width: 26, height: 26, borderRadius: '50%',
    background: 'rgba(124,58,237,.18)', border: '1px solid rgba(124,58,237,.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--violetbr)'
  },
  arrow: { color: 'rgba(255,255,255,.2)', fontSize: 18 },
  features: { display: 'flex', gap: 16, marginBottom: 52, flexWrap: 'wrap', justifyContent: 'center' },
  featureCard: {
    background: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 20, padding: 24, width: 210, textAlign: 'center',
    transition: 'border-color .2s, box-shadow .2s'
  },
  featureIcon: { fontSize: 30, marginBottom: 10 },
  featureName: {
    fontFamily: 'var(--ff-display)', fontSize: 14, fontWeight: 700,
    color: 'var(--txt)', marginBottom: 6
  },
  featureDesc: { fontSize: 12, color: 'var(--txt2)', lineHeight: 1.5 },
  btn: {
    background: 'linear-gradient(135deg,var(--violet) 0%,#6d28d9 100%)',
    color: '#fff', border: 'none', borderRadius: 14,
    padding: '18px 52px', fontFamily: 'var(--ff-display)',
    fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em',
    boxShadow: '0 0 32px rgba(124,58,237,.45)',
    transition: 'transform .2s, box-shadow .2s', marginBottom: 14,
    position: 'relative', overflow: 'hidden'
  },
  subnote: { fontFamily: 'var(--ff-mono)', fontSize: 12, color: 'var(--txt3)' }
};

const features = [
  { icon: '🔐', name: 'CipherCore', desc: 'End-to-end encryption from data ingestion to model deployment' },
  { icon: '🔍', name: 'ProbeScan',  desc: 'Automated GenAI vulnerability scanning across your ecosystem' },
  { icon: '🧠', name: 'GuardMind',  desc: 'Real-time policy enforcement and AI agent threat detection' },
];

export default function Landing({ onStart }) {
  return (
    <div style={s.wrap}>
      <div style={s.grid} />
      <div style={s.orb1} />
      <div style={s.orb2} />
      <div style={s.inner} className="fadeup">
        <div style={s.badge}><div style={s.dot} />GenAI Security Intelligence Platform v1.0</div>
        <div style={s.logo}>SentinelAI</div>
        <div style={s.tagline}>Detect · Assess · Fortify</div>
        <p style={s.desc}>
          The <strong style={{ color: 'var(--txt)' }}>first AI-native risk assessment platform</strong> for GenAI systems.
          Map your complete attack surface — from model config to agent governance — and get a
          board-ready security plan in under 5 minutes.
        </p>
        <div style={s.steps}>
          {['Estimate Your GenAI Risk', 'Optimise Security Posture', 'Get Your Security Plan'].map((label, i) => (
            <React.Fragment key={i}>
              <div style={s.stepItem}><div style={s.stepNum}>{i + 1}</div>{label}</div>
              {i < 2 && <div style={s.arrow}>→</div>}
            </React.Fragment>
          ))}
        </div>
        <div style={s.features}>
          {features.map((f, i) => (
            <div key={i} style={{ ...s.featureCard, animation: `float 3s ${i}s ease-in-out infinite` }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,58,237,.4)'; e.currentTarget.style.boxShadow = '0 0 30px rgba(124,58,237,.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={s.featureIcon}>{f.icon}</div>
              <div style={s.featureName}>{f.name}</div>
              <div style={s.featureDesc}>{f.desc}</div>
            </div>
          ))}
        </div>
        <button style={s.btn} onClick={onStart}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(124,58,237,.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 32px rgba(124,58,237,.45)'; }}>
          Start Risk Assessment →
        </button>
        <div style={s.subnote}>Powered by Claude AI · Bring your own API key · Free to use</div>
        <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.06)', width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.04em' }}>Designed &amp; built by <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>Arunakumar Tavva</span></div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.03em' }}>&copy; {new Date().getFullYear()} SentinelAI &middot; All rights reserved</div>
        </div>

      </div>
    </div>
  );
}
